import { Component, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import clsx from "clsx";
import {
  ApiError,
  fetchLmeMatch,
  fetchLmeState,
} from "../../api";
import type {
  MatchedLender,
  MatchResponse,
  Scenario,
  ScenarioSnapshot,
  StateResponse,
  Tier,
} from "../../types/lme";
import { TabPanel } from "./TabPanel";
import { LmeFinalizeModal } from "./LmeFinalizeModal";
import { LmeDraftPreviewModal } from "./LmeDraftPreviewModal";
import { LmeScenarioEditDialog } from "./LmeScenarioEditDialog";

/** LME-3 — diff the as-finalized scenario snapshot against the current inputs. */
type Change = { label: string; from: string; to: string };
const STALE_FIELDS: Array<{ key: keyof ScenarioSnapshot; label: string; money?: boolean }> = [
  { key: "loanAmount", label: "Loan amount", money: true },
  { key: "loanProduct", label: "Loan product" },
  { key: "lienPosition", label: "Lien position" },
  { key: "fico", label: "FICO" },
  { key: "dscr", label: "DSCR" },
  { key: "propType", label: "Property type" },
  { key: "propState", label: "Property state" },
  { key: "occupancy", label: "Occupancy" },
  { key: "termMin", label: "Term min (mo)" },
  { key: "termMax", label: "Term max (mo)" },
  { key: "speedDays", label: "Speed to close (days)" },
];
function fmtStale(v: string | number | null | undefined, money?: boolean): string {
  if (v == null || v === "") return "(none)";
  if (money) return "$" + Number(v).toLocaleString();
  return String(v);
}
function diffSnapshot(snap: ScenarioSnapshot, cur: Scenario): Change[] {
  const norm = (v: unknown) => (v == null ? "" : String(v));
  const out: Change[] = [];
  for (const f of STALE_FIELDS) {
    const a = snap[f.key] as string | number | null;
    const b = cur[f.key] as string | number | null;
    if (norm(a) !== norm(b)) {
      out.push({ label: f.label, from: fmtStale(a, f.money), to: fmtStale(b, f.money) });
    }
  }
  return out;
}

/**
 * Addition 1: build the auto-revision for "Update email to match" from the
 * input deltas. Runs through the LME-2a anchored "Change all" path — updates the
 * stale figures (and derived LTV/CLTV/LTARV) in place, nothing else.
 */
function buildUpdateRevision(changes: Change[]): string {
  const lines = changes.map((c) => `- ${c.label}: ${c.from} → ${c.to}`).join("\n");
  return (
    `The deal inputs changed since this email was last finalized:\n${lines}\n\n` +
    `Update every figure in the email that reflects these new inputs — including ` +
    `any LTV, CLTV, or LTARV that derive from them (use the deal data for the ` +
    `current values). Change nothing else: keep all wording, structure, and every ` +
    `other value exactly as they are now.`
  );
}

/**
 * Crash boundary mirroring Doc Portal's pattern — catches contract drift
 * (Edge-side type mismatch) and renders a clear pending pane instead of
 * blanking the page. Keeps the rest of Focus Mode usable while LME catches up.
 */
class LmeErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err: Error) {
    console.error("[LmeTab] render error:", err);
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

const TIER_LABEL: Record<Tier, string> = {
  strong: "STRONG",
  good: "GOOD",
  possible: "POSSIBLE",
  weak: "WEAK",
  disq: "DISQ",
};

interface LmeTabProps {
  dealId: string;
}

export function LmeTab({ dealId }: LmeTabProps) {
  const [match, setMatch] = useState<MatchResponse | null>(null);
  const [state, setState] = useState<StateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeDqs, setIncludeDqs] = useState(false);
  const [expandedLender, setExpandedLender] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    | { kind: "draft"; lender: MatchedLender }
    | { kind: "finalize"; initialRevision?: string }
    | { kind: "scenario-edit" }
    | null
  >(null);
  const [boundaryKey, setBoundaryKey] = useState(0);

  const load = useCallback(
    async (mode: "initial" | "manual") => {
      if (mode === "manual") setRefreshing(true);
      try {
        const [m, s] = await Promise.all([
          fetchLmeMatch({ deal_id: dealId, include_disqualified: includeDqs }),
          fetchLmeState(dealId),
        ]);
        setMatch(m);
        setState(s);
        setError(null);
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? formatApiErr(err)
            : err instanceof Error
              ? err.message
              : "fetch_failed";
        setError(msg);
      } finally {
        if (mode === "manual") setRefreshing(false);
        if (mode === "initial") setLoading(false);
      }
    },
    [dealId, includeDqs],
  );

  useEffect(() => {
    setLoading(true);
    setMatch(null);
    setState(null);
    setError(null);
    void load("initial");
  }, [dealId, load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // LME-3: a finalized email is "stale" if any input changed since it was
  // finalized. Needs the snapshot (absent on pre-LME-3 emails → no flag).
  const staleness = useMemo<Change[] | null>(() => {
    if (!match || !state?.has_finalized_email || !state.finalized_scenario_snapshot) {
      return null;
    }
    const changed = diffSnapshot(state.finalized_scenario_snapshot, match.deal_snapshot.scenario);
    return changed.length ? changed : null;
  }, [match, state]);

  if (loading) {
    return (
      <TabPanel>
        <div className="lme-loading">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-label">SCORING LENDERS</span>
        </div>
      </TabPanel>
    );
  }

  if (error) {
    return (
      <TabPanel>
        <div className="lme-error">
          ⚠ {error}
          <button
            className="lme-retry"
            onClick={() => {
              setError(null);
              setLoading(true);
              void load("initial");
            }}
          >
            RETRY
          </button>
        </div>
      </TabPanel>
    );
  }

  if (!match) {
    return (
      <TabPanel>
        <div className="lme-error">⚠ No match data returned.</div>
      </TabPanel>
    );
  }

  return (
    <LmeErrorBoundary
      key={`${dealId}:${boundaryKey}`}
      fallback={
        <TabPanel>
          <div className="lme-error">
            ⚠ LME render error — likely contract drift. Refresh to retry.
            <button
              className="lme-retry"
              onClick={() => {
                setBoundaryKey((k) => k + 1);
                void load("manual");
              }}
            >
              RETRY
            </button>
          </div>
        </TabPanel>
      }
    >
      <TabPanel>
        <LmeBody
          match={match}
          state={state}
          staleness={staleness}
          refreshing={refreshing}
          includeDqs={includeDqs}
          expandedLender={expandedLender}
          onEditScenario={() => setDialog({ kind: "scenario-edit" })}
          onUpdateEmail={() => {
            if (staleness) {
              setDialog({ kind: "finalize", initialRevision: buildUpdateRevision(staleness) });
            }
          }}
          onToggleDqs={() => {
            setIncludeDqs((v) => !v);
            // refetch with new include_disqualified
            setTimeout(() => void load("manual"), 0);
          }}
          onToggleExpand={(id) =>
            setExpandedLender((cur) => (cur === id ? null : id))
          }
          onRefresh={() => void load("manual")}
          onFinalize={() => setDialog({ kind: "finalize" })}
          onDraftEmail={(lender) => setDialog({ kind: "draft", lender })}
        />
        {dialog?.kind === "draft" && (
          <LmeDraftPreviewModal
            dealId={dealId}
            lender={dialog.lender}
            onCancel={() => setDialog(null)}
            onToast={(msg) => setToast(msg)}
            onSent={() => {
              setDialog(null);
              void load("manual");
            }}
          />
        )}
        {dialog?.kind === "scenario-edit" && (
          <LmeScenarioEditDialog
            dealId={dealId}
            scenario={match.deal_snapshot.scenario}
            onCancel={() => setDialog(null)}
            onSaved={(n) => {
              setDialog(null);
              setToast(`✓ Updated ${n} field${n === 1 ? "" : "s"} — re-scoring`);
              void load("manual");
            }}
          />
        )}
        {dialog?.kind === "finalize" && (
          <LmeFinalizeModal
            dealId={dealId}
            selectedLenderIds={match.lenders.slice(0, 5).map((l) => l.account_id)}
            initialScenario={match.deal_snapshot.scenario}
            initialRevision={dialog.initialRevision}
            onClose={() => {
              setDialog(null);
              void load("manual");
            }}
            onToast={(msg) => setToast(msg)}
          />
        )}
        {toast && <div className="edge-toast">{toast}</div>}
      </TabPanel>
    </LmeErrorBoundary>
  );
}

interface LmeBodyProps {
  match: MatchResponse;
  state: StateResponse | null;
  staleness: Change[] | null;
  refreshing: boolean;
  includeDqs: boolean;
  expandedLender: string | null;
  onEditScenario: () => void;
  onUpdateEmail: () => void;
  onToggleDqs: () => void;
  onToggleExpand: (lenderId: string) => void;
  onRefresh: () => void;
  onFinalize: () => void;
  onDraftEmail: (lender: MatchedLender) => void;
}

function LmeBody({
  match,
  state,
  staleness,
  refreshing,
  includeDqs,
  expandedLender,
  onEditScenario,
  onUpdateEmail,
  onToggleDqs,
  onToggleExpand,
  onRefresh,
  onFinalize,
  onDraftEmail,
}: LmeBodyProps) {
  const survivors = match.lenders.filter((l) => l.tier !== "disq");
  const dqs = match.lenders.filter((l) => l.tier === "disq");
  const tc = match.summary.tier_counts;

  return (
    <div className="lme-tab">
      <div className="lme-header">
        <div className="lme-state-pill-row">
          <StatePill state={state} />
        </div>
        <div className="lme-header-actions">
          <button
            className="lme-finalize-btn"
            onClick={onFinalize}
            title="Generate finalized email content with Claude"
          >
            ✦ FINALIZE
          </button>
          <button
            className="lme-refresh"
            onClick={onRefresh}
            disabled={refreshing}
            title="Re-score lenders against the current deal"
          >
            {refreshing ? "↻ …" : "↻ REFRESH"}
          </button>
        </div>
      </div>

      <DealSnapshot match={match} onEdit={onEditScenario} />

      {staleness && <StalenessBanner changes={staleness} onUpdate={onUpdateEmail} />}

      <div className="lme-tier-summary">
        <span className="lme-tier-summary-label">RANKING</span>
        <TierChip n={tc.strong} label="STRONG" cls="dp-status-approved" />
        <TierChip n={tc.good} label="GOOD" cls="dp-status-uploaded" />
        <TierChip n={tc.possible} label="POSSIBLE" cls="dp-status-revision_requested" />
        <TierChip n={tc.weak} label="WEAK" cls="dp-status-needed" />
        <span className="lme-tier-summary-meta">
          · {match.summary.total_lenders_evaluated} evaluated · {match.summary.past_closings_lender_count} past-closings
        </span>
      </div>

      <div className="lme-lender-list">
        {survivors.length === 0 && (
          <div className="lme-empty">
            No survivors — every lender hit a hard rule. Toggle disqualified below to see why.
          </div>
        )}
        {survivors.map((l) => (
          <LenderRow
            key={l.account_id}
            lender={l}
            expanded={expandedLender === l.account_id}
            onToggle={() => onToggleExpand(l.account_id)}
            onDraft={() => onDraftEmail(l)}
          />
        ))}
      </div>

      <button className="lme-dq-toggle" onClick={onToggleDqs}>
        {includeDqs ? "▾ HIDE" : "▸ SHOW"} DISQUALIFIED ({tc.disqualified})
      </button>
      {includeDqs && (
        <div className="lme-lender-list lme-lender-list-dq">
          {dqs.map((l) => (
            <LenderRow
              key={l.account_id}
              lender={l}
              expanded={expandedLender === l.account_id}
              onToggle={() => onToggleExpand(l.account_id)}
              onDraft={() => onDraftEmail(l)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatePill({ state }: { state: StateResponse | null }) {
  if (!state) {
    return <div className="lme-state-pill lme-state-pill-empty">STATE UNAVAILABLE</div>;
  }
  if (!state.has_finalized_email) {
    return (
      <div className="lme-state-pill lme-state-pill-empty">
        NO FINALIZED EMAIL · {state.lenders_pitched_count} pitches logged
      </div>
    );
  }
  return (
    <div className="lme-state-pill lme-state-pill-finalized">
      <span className="lme-state-pill-ver">FINALIZED v{state.current_version}</span>
      <span className="lme-state-pill-meta">
        · {state.lenders_pitched_unique} lenders pitched · {state.sent_with_dropbox_count} sent
        {state.email_log_unparseable_lines > 0 && (
          <> · {state.email_log_unparseable_lines} unparseable log line(s)</>
        )}
      </span>
    </div>
  );
}

function DealSnapshot({ match, onEdit }: { match: MatchResponse; onEdit: () => void }) {
  const s = match.deal_snapshot.scenario;
  const chips: Array<{ label: string; value: string }> = [];
  if (s.loanAmount) chips.push({ label: "AMT", value: "$" + s.loanAmount.toLocaleString() });
  if (s.loanProduct) chips.push({ label: "PRODUCT", value: s.loanProduct });
  if (s.lienPosition) chips.push({ label: "LIEN", value: s.lienPosition });
  if (s.ltv != null) chips.push({ label: "LTV", value: s.ltv + "%" });
  if (s.cltvDriven && s.cltv != null) chips.push({ label: "CLTV", value: s.cltv + "%" });
  if (s.ltarv != null) chips.push({ label: "LTARV", value: s.ltarv + "%" });
  if (s.fico != null) chips.push({ label: "FICO", value: String(s.fico) });
  if (s.dscr != null) chips.push({ label: "DSCR", value: String(s.dscr) });
  if (s.propType) chips.push({ label: "TYPE", value: s.propType });
  if (s.propState) chips.push({ label: "ST", value: s.propState });
  if (s.occupancy) chips.push({ label: "OCC", value: s.occupancy });
  if (s.termMin || s.termMax)
    chips.push({
      label: "TERM",
      value: `${s.termMin ?? "?"}–${s.termMax ?? "?"}mo`,
    });
  if (s.speedDays != null) chips.push({ label: "SPEED", value: s.speedDays + "d" });

  return (
    <div className="lme-deal-snapshot">
      <div className="lme-deal-snapshot-head">
        <div className="lme-deal-snapshot-name">{match.deal_snapshot.deal_name}</div>
        <button
          className="lme-scenario-edit-btn"
          onClick={onEdit}
          title="Edit the scenario inputs (re-scores lenders)"
        >
          ✎ EDIT
        </button>
      </div>
      <div className="lme-deal-snapshot-chips">
        <span
          className="lme-chip lme-chip-measure"
          title={
            s.cltvDriven
              ? "An existing lien survives close — ranked on combined exposure (CLTV) vs each lender's Max CLTV."
              : "New loan is in 1st position — ranked on LTV vs each lender's Max LTV."
          }
        >
          <span className="lme-chip-label">SCORED ON</span>
          <span className="lme-chip-value">{s.cltvDriven ? "CLTV" : "LTV"}</span>
        </span>
        {chips.map((c) => (
          <span className="lme-chip" key={c.label}>
            <span className="lme-chip-label">{c.label}</span>
            <span className="lme-chip-value">{c.value}</span>
          </span>
        ))}
        {match.deal_snapshot.scenario_overrides_applied.length > 0 && (
          <span className="lme-chip lme-chip-override">
            <span className="lme-chip-label">OVERRIDES</span>
            <span className="lme-chip-value">
              {match.deal_snapshot.scenario_overrides_applied.join(", ")}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function TierChip({ n, label, cls }: { n: number; label: string; cls: string }) {
  return (
    <span className={clsx("lme-tier-chip", cls)}>
      <span className="lme-tier-chip-n">{n}</span> {label}
    </span>
  );
}

interface LenderRowProps {
  lender: MatchedLender;
  expanded: boolean;
  onToggle: () => void;
  onDraft: () => void;
}

/** LME-3 — non-blocking notice that inputs changed since the email was finalized.
 *  Click to expand exactly what changed (same before→after format as the gate). */
function StalenessBanner({
  changes,
  onUpdate,
}: {
  changes: Change[];
  onUpdate: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="lme-stale-banner">
      <div className="lme-stale-banner-head">
        <button
          className="lme-stale-banner-toggle-btn"
          onClick={() => setOpen((o) => !o)}
          title="The finalized email is based on older inputs"
        >
          <span className="lme-stale-banner-text">
            ⚠ Scenario edited since this email was finalized.
          </span>
          <span className="lme-stale-banner-toggle">
            {open ? "▾ hide" : `▸ ${changes.length} change${changes.length === 1 ? "" : "s"}`}
          </span>
        </button>
        <button
          className="lme-stale-banner-update"
          onClick={onUpdate}
          title="Open finalize with an auto-built revision to update the changed figures (you review + finalize)"
        >
          ↻ UPDATE EMAIL TO MATCH
        </button>
      </div>
      {open && (
        <div className="lme-stale-banner-changes">
          {changes.map((ch, i) => (
            <div className="write-confirm-change" key={i}>
              <span className="write-confirm-change-label">{ch.label}</span>
              <span className="write-confirm-change-from">{ch.from}</span>
              <span className="write-confirm-change-arrow">→</span>
              <span className="write-confirm-change-to">{ch.to}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LenderRow({ lender, expanded, onToggle, onDraft }: LenderRowProps) {
  const tierCls = `lme-tier-${lender.tier}`;
  const positive = lender.reasons.filter((r) => r.type === "pos");
  const warnings = lender.reasons.filter((r) => r.type === "neg");
  return (
    <div className={clsx("lme-lender-row", expanded && "expanded", tierCls)}>
      <div className="lme-lender-main" onClick={onToggle}>
        <span className={clsx("lme-tier-pill", tierCls)}>
          {TIER_LABEL[lender.tier]} {lender.score}
        </span>
        <span className="lme-lender-name">{lender.account_name}</span>
        {lender.primary_contact && (
          <span className="lme-lender-contact">
            {lender.primary_contact.name}
            {lender.primary_contact.role ? ` · ${lender.primary_contact.role}` : ""}
          </span>
        )}
        {lender.matrix_age_days != null && (
          <span className="lme-lender-matrix">
            matrix {Math.round(lender.matrix_age_days / 30)}mo
          </span>
        )}
        <button
          className="lme-lender-draft"
          onClick={(e) => {
            e.stopPropagation();
            onDraft();
          }}
          title="Draft + preview the per-lender email"
        >
          DRAFT EMAIL
        </button>
        <span className="lme-lender-expand">▸</span>
      </div>
      {expanded && (
        <div className="lme-lender-detail">
          {lender.disqualifiers.length > 0 && (
            <div className="lme-reason-block">
              <div className="lme-reason-block-label">DISQUALIFIED</div>
              {lender.disqualifiers.map((d, i) => (
                <div className="lme-reason lme-reason-dq" key={i}>
                  ✕ {d}
                </div>
              ))}
            </div>
          )}
          {positive.length > 0 && (
            <div className="lme-reason-block">
              <div className="lme-reason-block-label">FIT</div>
              {positive.map((r, i) => (
                <div className="lme-reason lme-reason-pos" key={i}>
                  ✓ {r.text}
                </div>
              ))}
            </div>
          )}
          {warnings.length > 0 && (
            <div className="lme-reason-block">
              <div className="lme-reason-block-label">WARNINGS</div>
              {warnings.map((r, i) => (
                <div className="lme-reason lme-reason-neg" key={i}>
                  ⚠ {r.text}
                </div>
              ))}
            </div>
          )}
          {lender.lender_notes_quirks && (
            <div className="lme-reason-block lme-reason-block-notes">
              <div className="lme-reason-block-label">QUIRKS</div>
              <div className="lme-lender-quirks">{lender.lender_notes_quirks}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatApiErr(err: ApiError): string {
  const body = err.body as { error?: { code?: string; message?: string } } | undefined;
  if (body?.error) {
    return `${body.error.code ?? "ERROR"}: ${body.error.message ?? "no message"} (HTTP ${err.status})`;
  }
  return `${err.code} (HTTP ${err.status})`;
}
